import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import {
    Badge,
    Button,
    Divider,
    Menu,
    MenuItem,
    Popover,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';

import Visibility from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import RoomIcon from '@mui/icons-material/Room';

import MDBox from 'components/MDBox';

import moment from 'moment/moment';
import { gpsStatusBadgeColor } from 'utils/constant';

import {
    TableVirtuoso
} from 'react-virtuoso';

import "react-virtualized/styles.css";

import { TablePaginationActions } from '../CustomPaginationTable';
import crudsService from 'services/cruds-service';
import { useMaterialUIController } from 'context';

/*
|--------------------------------------------------------------------------
| Column Header
|--------------------------------------------------------------------------
*/

function DataGridStyleHeader({
    column,
    sort,
    onSort,
    onFilter,
    onHideColumn,
    hasFilter,
    onOpenColumnMenu
}) {
    const isSorted = sort?.field === column.accessor;

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                gap: 0.5
            }}
        >
            <Box
                onClick={() => {
                    if (column.accessor !== 'actions') {
                        onSort(column.accessor);
                    }
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                    flex: 1,
                    cursor:
                        column.accessor === 'actions'
                            ? 'default'
                            : 'pointer'
                }}
            >
                <Typography
                    component="span"
                    sx={{
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    }}
                >
                    {column.Header}
                </Typography>

                {column.accessor !== 'actions' && (
                    <>
                        {isSorted ? (
                            sort.direction === 'asc' ? (
                                <ArrowUpwardIcon
                                    sx={{
                                        fontSize: 15,
                                        opacity: 0.9,
                                        color: 'primary.main'
                                    }}
                                />
                            ) : (
                                <ArrowDownwardIcon
                                    sx={{
                                        fontSize: 15,
                                        opacity: 0.9,
                                        color: 'primary.main'
                                    }}
                                />
                            )
                        ) : (
                            <UnfoldMoreIcon
                                sx={{
                                    fontSize: 16,
                                    opacity: 0.45
                                }}
                            />
                        )}
                    </>
                )}

                {hasFilter && (
                    <FilterListIcon
                        sx={{
                            fontSize: 15,
                            opacity: 1
                        }}
                    />
                )}
            </Box>

            {column.accessor !== 'actions' && (
                <IconButton
                    size="small"
                    onClick={(event) => {
                        event.stopPropagation();
                        onOpenColumnMenu(event, column);
                    }}
                    sx={{
                        color: 'inherit',
                        padding: '2px'
                    }}
                >
                    <MoreVertIcon sx={{ fontSize: 17 }} />
                </IconButton>
            )}
        </Box>
    );
}

/*
|--------------------------------------------------------------------------
| Filter Popover
|--------------------------------------------------------------------------
*/

function ColumnFilterPopover({
    anchorEl,
    column,
    value,
    onChange,
    onApply,
    onClear,
    onClose
}) {
    if (!column) {
        return null;
    }

    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left'
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
            }}
            slotProps={{
                paper: {
                    sx: {
                        width: 280,
                        backgroundColor: !darkMode ? "white !important" : "#202940 !important",
                        color: darkMode ? "white !important" : "black !important",
                    }
                }
            }}
        >
            <Typography
                sx={{
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    mb: 1.5
                }}
            >
                Filter {column.Header}
            </Typography>

            <TextField
                fullWidth
                size="small"
                value={value || ''}
                onChange={(event) =>
                    onChange(event.target.value)
                }
                placeholder={`Search ${column.Header}`}
                autoFocus
            />

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 2
                }}
            >
                <Button
                    size="small"
                    onClick={onClear}
                >
                    Clear
                </Button>

                <Button
                    variant="contained"
                    size="small"
                    onClick={onApply}
                >
                    Apply
                </Button>
            </Box>
        </Popover>
    );
}

/*
|--------------------------------------------------------------------------
| Row
|--------------------------------------------------------------------------
*/

function Row(props) {
    const {
        group,
        columns,
        subColumns,
        clickExportHandler,
        clickViewHandler,
        clickDeleteHandler,
        clickInfoHandler,
        clickSubRouteViewHandler
    } = props;

    const [open, setOpen] = React.useState(false);
    const [trackingPointData, setTrackingPointData] = React.useState([]);
    const [loader, setLoader] = React.useState(false);

    const openCollapse = async (value) => {
        setOpen(value);

        if (!value) {
            return;
        }

        setLoader(true);

        try {
            const resp = await crudsService.getTrackingPoint(
                group.header.route_id
            );

            if (resp?.success) {
                setTrackingPointData(resp.data || []);
            }
        } catch (error) {
            console.error('openCollapse', error);
        } finally {
            setLoader(false);
        }
    };


    function Loading() {
        return (
            <Stack
                spacing={1}
                sx={{
                    p: 2
                }}
            >
                <Skeleton
                    variant="rectangular"
                    height={30}
                />

                <Skeleton
                    variant="text"
                    sx={{ fontSize: '1rem' }}
                />

                <Skeleton
                    variant="text"
                    sx={{ fontSize: '1rem' }}
                />

                <Skeleton
                    variant="text"
                    sx={{ fontSize: '1rem' }}
                />

                <Skeleton
                    variant="text"
                    sx={{ fontSize: '1rem' }}
                />
            </Stack>
        );
    }


    const renderTrackingPointCell = (
        column,
        child,
        index
    ) => {
        const accessor = column.accessor;

        const commonProps = {
            key: accessor,
            sx: {
                paddingTop: '0px !important',
                paddingBottom: '0px !important',
                fontSize: '0.875rem',
                borderBottom: '0.5px solid #ced4da',
                color: '#ffffff',
                width: column.width,
                padding: '10px 4px',
                textAlign: column.align
            }
        };


        /*
        |--------------------------------------------------------------------------
        | Actions
        |--------------------------------------------------------------------------
        */

        if (accessor === 'actions') {
            return (
                <TableCell {...commonProps}>
                    <Box
                        display="flex"
                        alignItems="center"
                    >
                        <Tooltip title="View Info">
                            <IconButton
                                size="small"
                                onClick={() =>
                                    clickSubRouteViewHandler(child)
                                }
                            >
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Standard location fields
        |--------------------------------------------------------------------------
        */

        if (
            [
                'latitude',
                'longitude',
                'heading',
                'timestamp'
            ].includes(accessor)
        ) {
            return (
                <TableCell {...commonProps}>
                    {accessor === 'timestamp'
                        ? moment(child[accessor]).format(
                            'MMMM DD, YYYY h:mm:ss A'
                        )
                        : child[accessor]}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Status
        |--------------------------------------------------------------------------
        */

        if (accessor === 'status') {
            return (
                <TableCell {...commonProps}>
                    {child.status ? (
                        <Badge
                            color={gpsStatusBadgeColor(
                                child.status
                            )}
                            variant="standard"
                            badgeContent={child.status}
                            sx={{
                                ml: 2.5
                            }}
                        />
                    ) : (
                        ''
                    )}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Speed
        |--------------------------------------------------------------------------
        */

        if (accessor === 'speed') {
            return (
                <TableCell {...commonProps}>
                    {
                        child
                            .DeviceMonitorParameterValue
                            ?.value
                            ?.convert_position_speed
                    }{' '}
                    mph
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Geofence
        |--------------------------------------------------------------------------
        */

        if (
            accessor === 'geofence_id' &&
            child.geofence_id
        ) {
            return (
                <TableCell {...commonProps}>
                    {child.Geofence?.name || ''}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Geofence event
        |--------------------------------------------------------------------------
        */

        if (
            accessor === 'geofence_event_id' &&
            child.geofence_event_id
        ) {
            return (
                <TableCell {...commonProps}>
                    {child.GeofenceEvent?.event_type || ''}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Ignition
        |--------------------------------------------------------------------------
        */

        if (accessor === 'ignition_on') {
            return (
                <TableCell {...commonProps}>
                    {child.ignition_on !== 0
                        ? 'True'
                        : 'False'}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Point ID
        |--------------------------------------------------------------------------
        */

        if (accessor === 'point_id') {
            return (
                <TableCell {...commonProps}>
                    {child.point_id}{' '}

                    {index === 0 && (
                        <RoomIcon
                            titleAccess="Started Route"
                            sx={{
                                color: 'green',
                                fontSize:
                                    '20px !important'
                            }}
                        />
                    )}

                    {index ===
                        trackingPointData.length - 1 &&
                        group.header?.end_time && (
                            <RoomIcon
                                sx={{
                                    color: 'red',
                                    fontSize:
                                        '20px !important'
                                }}
                            />
                        )}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Place type
        |--------------------------------------------------------------------------
        */

        if (accessor === 'place_type') {
            return (
                <TableCell {...commonProps}>
                    {child
                        .DeviceMonitorParameterValue
                        ?.value
                        ?.reverse_geocoded_address
                        ?.types
                        ?.join(', ') || ''}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | Place location
        |--------------------------------------------------------------------------
        */

        if (accessor === 'place_location') {
            return (
                <TableCell {...commonProps}>
                    {child
                        .DeviceMonitorParameterValue
                        ?.value
                        ?.reverse_geocoded_address
                        ?.formatted_address ??
                        ''}
                </TableCell>
            );
        }


        /*
        |--------------------------------------------------------------------------
        | CAN fields
        |--------------------------------------------------------------------------
        */

        if (
            [
                'can_engine_rpm',
                'can_fuel_level',
                'convert_can_intake_map',
                'can_engine_load_level',
                'can_throttle_pedal_level',
                'convert_can_intake_air_temperature',
                'convert_can_engine_coolant_temperature',
                'vehicle_vin'
            ].includes(accessor)
        ) {
            return (
                <TableCell {...commonProps}>
                    {
                        child
                            .DeviceMonitorParameterValue
                            ?.value?.[accessor] ?? ''
                    }
                </TableCell>
            );
        }


        return (
            <TableCell {...commonProps}>
                {child[accessor] ?? ''}
            </TableCell>
        );
    };


    return (
        <React.Fragment>

            {/* Main Route Row */}

            <TableRow
                sx={{
                    '& > *': {
                        borderBottom: 'unset'
                    }
                }}
            >
                <TableCell
                    sx={{
                        width: 55,
                        padding: '4px'
                    }}
                >
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() =>
                            openCollapse(!open)
                        }
                    >
                        {open ? (
                            <KeyboardArrowUpIcon />
                        ) : (
                            <KeyboardArrowDownIcon />
                        )}
                    </IconButton>
                </TableCell>


                {columns.map((column) => (
                    <TableCell
                        key={column.accessor}
                       sx={{
                            width: `${column.width}px`,
                            minWidth: `${column.width}px`,
                            maxWidth: `${column.width}px`,
                            fontSize: '0.875rem',
                            fontWeight: 400,
                            color: '#ffffff',
                            opacity: 1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            boxSizing: 'border-box'
                        }}
                    >
                        {column.accessor === 'actions' ? (

                            <Box
                                display="flex"
                                alignItems="center"
                            >
                                <Tooltip title="Info Route">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            clickInfoHandler(
                                                group.header
                                            )
                                        }
                                    >
                                        <InfoIcon />
                                    </IconButton>
                                </Tooltip>


                                <Tooltip title="View Route">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            clickViewHandler(
                                                group.header
                                            )
                                        }
                                    >
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>


                                <Tooltip title="Export Route">
                                    <IconButton
                                        size="small"
                                        onClick={() =>
                                            clickExportHandler(
                                                group.header.route_id
                                            )
                                        }
                                    >
                                        <FileDownloadIcon />
                                    </IconButton>
                                </Tooltip>


                                {clickDeleteHandler && (
                                    <Tooltip title="Delete Route">
                                        <IconButton
                                            size="small"
                                            onClick={(event) =>
                                                clickDeleteHandler(
                                                    event,
                                                    group.header
                                                        .route_id
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                        ) : column?.Cell ? (

                            column.Cell(
                                group.header[
                                    column.accessor
                                ],
                                group.header
                            )

                        ) : (

                            group.header[
                                column.accessor
                            ]

                        )}
                    </TableCell>
                ))}
            </TableRow>


            {/* Collapse */}

            <TableRow>
                <TableCell
                    sx={{
                        paddingBottom: 0,
                        paddingTop: 0
                    }}
                    colSpan={columns.length + 1}
                >
                    <Collapse
                        in={open}
                        timeout="auto"
                        unmountOnExit
                    >
                        <Box
                            sx={{
                                width: '100%'
                            }}
                        >
                            {loader ? (

                                <Loading />

                            ) : (

                                <TableContainer
                                    component={Paper}
                                    sx={{
                                        overflowX: 'auto',
                                        margin: 1,
                                        width: 'calc(100% - 16px)'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            minWidth: '2180px',
                                            height: '600px'
                                        }}
                                    >
                                        <TableVirtuoso
                                            data={
                                                trackingPointData
                                            }

                                            fixedHeaderContent={() => (
                                                <TableRow>
                                                    {subColumns
                                                        .filter(
                                                            column =>
                                                                column.show !==
                                                                false
                                                        )
                                                        .map(
                                                            column => (
                                                                <TableCell
                                                                    key={
                                                                        column.accessor
                                                                    }
                                                                    variant="head"
                                                                    sx={{
                                                                        textAlign:
                                                                            column.align,
                                                                        width:
                                                                            column.width,
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                                                    {
                                                                        column.Header
                                                                    }
                                                                </TableCell>
                                                            )
                                                        )}
                                                </TableRow>
                                            )}

                                            itemContent={(
                                                index,
                                                child
                                            ) =>
                                                subColumns
                                                    .filter(
                                                        column =>
                                                            column.show !==
                                                            false
                                                    )
                                                    .map(
                                                        column =>
                                                            renderTrackingPointCell(
                                                                column,
                                                                child,
                                                                index
                                                            )
                                                    )
                                            }

                                            components={{
                                                Table: (
                                                    props
                                                ) => (
                                                    <Table
                                                        {...props}
                                                        stickyHeader
                                                        sx={{
                                                            borderCollapse:
                                                                'separate',
                                                            tableLayout:
                                                                'auto',
                                                            minWidth:
                                                                '100%'
                                                        }}
                                                    />
                                                ),

                                                TableHead:
                                                    React.forwardRef(
                                                        (
                                                            props,
                                                            ref
                                                        ) => (
                                                            <TableHead
                                                                {...props}
                                                                ref={
                                                                    ref
                                                                }
                                                                sx={{
                                                                    display:
                                                                        'table-header-group !important'
                                                                }}
                                                            />
                                                        )
                                                    ),

                                                TableRow,

                                                TableBody:
                                                    React.forwardRef(
                                                        (
                                                            props,
                                                            ref
                                                        ) => (
                                                            <TableBody
                                                                {...props}
                                                                ref={
                                                                    ref
                                                                }
                                                            />
                                                        )
                                                    )
                                            }}
                                        />
                                    </Box>
                                </TableContainer>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </React.Fragment>
    );
}

/*
|--------------------------------------------------------------------------
| Main Component
|--------------------------------------------------------------------------
*/

const useStyles = makeStyles({
    customTableHead: {
        display: 'table-header-group!important', // Use the correct CSS property without quotes
    },
});

export default function RouteCollapsibleTable({
    data,
    columns,
    subColumns,
    clickViewHandler,
    clickExportHandler,
    clickDeleteHandler,
    openLogs,
    clickInfoHandler,
    clickSubRouteViewHandler,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    sortModel,
    onSortModelChange,
    onFilterModelChange,
    count
}) {

    /*
    |--------------------------------------------------------------------------
    | Column visibility
    |--------------------------------------------------------------------------
    */

    const getInitialVisibility = React.useCallback(
        () => {
            return columns.reduce(
                (result, column) => {
                    result[column.accessor] = column.show !== false;
                    return result;
                },
                {}
            );
        },
        [columns]
    );

    // ---------------------------------------------------------------------------
    // Column visibility persistence (mirrors device list column visibility)
    // ---------------------------------------------------------------------------
    const COLUMN_VISIBILITY_STORAGE_KEY = "rivio.routes.columnVisibility.v1";

    function readColumnVisibility() {
        try {
            const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error("readColumnVisibility", error);
            return null;
        }
    }

    const [columnVisibility, setColumnVisibility] = React.useState(
        () => readColumnVisibility() ?? getInitialVisibility()
    );

    // Persist column visibility changes
    React.useEffect(() => {
        try {
            localStorage.setItem(COLUMN_VISIBILITY_STORAGE_KEY, JSON.stringify(columnVisibility));
        } catch (error) {
            console.error("writeColumnVisibility", error);
        }
    }, [columnVisibility]);

    /*
    |--------------------------------------------------------------------------
    | Filters
    |--------------------------------------------------------------------------
    */

    const [filters, setFilters] = React.useState({});

    const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);

    const [activeFilterColumn, setActiveFilterColumn] = React.useState(null);

    const [temporaryFilterValue, setTemporaryFilterValue] = React.useState('');

    const classes = useStyles();

    /*
    |--------------------------------------------------------------------------
    | Column menu
    |--------------------------------------------------------------------------
    */

    const [columnMenuAnchorEl, setColumnMenuAnchorEl] = React.useState(null);

    const [columnMenuColumn, setColumnMenuColumn] = React.useState(null);

    /*
    |--------------------------------------------------------------------------
    | Sort
    |--------------------------------------------------------------------------
    */

    const handleSort = (field) => {
        onSortModelChange((previous) => {
            const isSameField = previous.field === field;
            const newDirection = isSameField && previous.direction === 'asc' ? 'desc' : 'asc';
            
            return { field, direction: newDirection };
        });
    };

    /*
    |--------------------------------------------------------------------------
    | Open filter
    |--------------------------------------------------------------------------
    */

    const handleOpenFilter = (
        event,
        column
    ) => {
        event?.stopPropagation?.();

        setActiveFilterColumn(column);

        setTemporaryFilterValue(
            filters[column.accessor] || ''
        );

        setFilterAnchorEl(
            event.currentTarget
        );

        setColumnMenuAnchorEl(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Apply filter
    |--------------------------------------------------------------------------
    */

    const handleApplyFilter = () => {
        if (!activeFilterColumn) {
            return;
        }

        const field = activeFilterColumn.accessor;
        const value = temporaryFilterValue.trim();

        setFilters((previous) => {
            const next = { ...previous };
            if (value.length === 0) {
                delete next[field];
            } else {
                next[field] = value;
            }
            
            // Sync with parent filter model
            const filterItems = Object.entries(next).map(([field, value]) => ({
                field,
                operator: 'contains',
                value
            }));
            onFilterModelChange({ items: filterItems });

            return next;
        });

        setFilterAnchorEl(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Clear filter
    |--------------------------------------------------------------------------
    */

    const handleClearFilter = () => {
        if (!activeFilterColumn) {
            return;
        }

        const field = activeFilterColumn.accessor;

        setFilters((previous) => {
            const next = { ...previous };
            delete next[field];
            
            // Sync with parent filter model
            const filterItems = Object.entries(next).map(([field, value]) => ({
                field,
                operator: 'contains',
                value
            }));
            onFilterModelChange({ items: filterItems });

            return next;
        });

        setTemporaryFilterValue('');
        setFilterAnchorEl(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Column menu
    |--------------------------------------------------------------------------
    */

    const handleOpenColumnMenu = (
        event,
        column
    ) => {
        event.stopPropagation();

        setColumnMenuAnchorEl(
            event.currentTarget
        );

        setColumnMenuColumn(column);

        setFilterAnchorEl(null);
    };

    const handleCloseColumnMenu = () => {
        setColumnMenuAnchorEl(null);
        setColumnMenuColumn(null);
    };

    /*
    |--------------------------------------------------------------------------
    | Hide column
    |--------------------------------------------------------------------------
    */

    const handleHideColumn = () => {

        if (!columnMenuColumn) {
            return;
        }

        setColumnVisibility(
            previous => ({
                ...previous,
                [columnMenuColumn.accessor]:
                    false
            })
        );

        handleCloseColumnMenu();
    };

    /*
    |--------------------------------------------------------------------------
    | Show all columns
    |--------------------------------------------------------------------------
    */

    const handleShowAllColumns = () => {

        const visibility = {};

        columns.forEach(column => {
            visibility[column.accessor] = true;
        });

        setColumnVisibility(visibility);
    };

    /*
    |--------------------------------------------------------------------------
    | Global search + filters
    |--------------------------------------------------------------------------
    */

    const filteredData = React.useMemo(() => {

        let result = [...data];
        return result;

    }, [
        data
    ]);

    /*
    |--------------------------------------------------------------------------
    | Visible columns
    |--------------------------------------------------------------------------
    */

    const visibleColumns =
        React.useMemo(() => {

            return columns.filter(
                column =>
                    columnVisibility[
                        column.accessor
                    ] !== false
            );

        }, [
            columns,
            columnVisibility
        ]);

    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const handleChangePage = (
        event,
        newPage
    ) => {
        setPage(newPage);
    };

    async function onRowsPerPageChange(
        event,
        value
    ) {
        event.stopPropagation();

        setRowsPerPage(value);
    }

    /*
    |--------------------------------------------------------------------------
    | Rows
    |--------------------------------------------------------------------------
    */

    const groupedData =
        React.useMemo(() => {

            return filteredData.map(
                element => ({
                    header: element,
                    children:
                        element.RouteTrackingPoints,
                    route_id:
                        element.route_id,
                    device_id:
                        element.device_id
                })
            );

        }, [filteredData]);

    /*
    |--------------------------------------------------------------------------
    | Active filter count
    |--------------------------------------------------------------------------
    */

    const activeFilterCount = Object.keys(filters).length;

    return (
        <TableContainer
            component={Paper}
            sx={{
                width: '100%'
            }}
        >

            <MDBox
                sx={{
                    width: '100%',
                    overflow: 'auto'
                }}
            >

                {/* =========================================================
                    Toolbar
                ========================================================= */}

                <Box
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom:
                            '1px solid rgba(255,255,255,0.12)'
                    }}
                >

                   <span></span>


                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >

                        {/* Active filter */}

                        {activeFilterCount > 0 && (
                            <Button
                                size="small"
                                startIcon={
                                    <FilterListIcon />
                                }
                                onClick={() =>
                                    setFilters({})
                                }
                            >
                                Clear Filters (
                                {activeFilterCount}
                                )
                            </Button>
                        )}


                        {/* Columns */}

                        <Button
                            size="small"
                            startIcon={
                                <ViewColumnIcon />
                            }
                            onClick={
                                handleShowAllColumns
                            }
                        >
                            Columns
                        </Button>

                    </Box>
                </Box>


                {/* =========================================================
                    Table
                ========================================================= */}

                <Table
                    aria-label="collapsible route table"
                    sx={{
                        width: 'max-content',
                        minWidth: '100%',
                        tableLayout: 'fixed'
                    }}
                >

                    {/* =====================================================
                        Header
                    ===================================================== */}

                    <TableHead classes={{ root: classes.customTableHead }}>

                        <TableRow
                            sx={{
                                backgroundColor:
                                    'rgba(255,255,255,0.04)',

                                '& .MuiTableCell-root': {
                                    borderBottom:
                                        '1px solid rgba(255,255,255,0.15)'
                                }
                            }}
                        >

                            {/* Expand */}

                            <TableCell
                                sx={{
                                    width: '55px',
                                    minWidth: '55px',
                                    maxWidth: '55px',
                                    padding: '8px'
                                }}
                            />


                            {visibleColumns.map(
                                column => (
                                    <TableCell
                                        key={
                                            column.accessor
                                        }
                                        sx={{
                                            width:
                                                column.width,
                                            minWidth:
                                                column.width,
                                            padding:
                                                '8px 10px',
                                            color:
                                                '#ffffff',
                                            opacity: 0.85
                                        }}
                                    >

                                        <DataGridStyleHeader
                                            column={column}

                                            sort={sortModel}

                                            onSort={
                                                handleSort
                                            }

                                            onFilter={
                                                handleOpenFilter
                                            }

                                            onHideColumn={
                                                () => {
                                                    setColumnVisibility(
                                                        previous => ({
                                                            ...previous,
                                                            [column.accessor]:
                                                                false
                                                        })
                                                    );
                                                }
                                            }

                                            hasFilter={
                                                Boolean(
                                                    filters[
                                                        column
                                                            .accessor
                                                    ]
                                                )
                                            }

                                            onOpenColumnMenu={
                                                handleOpenColumnMenu
                                            }
                                        />

                                    </TableCell>
                                )
                            )}

                        </TableRow>

                    </TableHead>


                    {/* =====================================================
                        Body
                    ===================================================== */}

                    <TableBody>

                        {groupedData.length === 0 ? (

                            <TableRow>

                                <TableCell
                                    colSpan={
                                        visibleColumns.length +
                                        1
                                    }
                                    align="center"
                                    sx={{
                                        py: 5,
                                        color:
                                            'text.secondary'
                                    }}
                                >
                                    No records found
                                </TableCell>

                            </TableRow>

                        ) : (

                            groupedData.map(group => (

                                <Row
                                    key={
                                        group.header
                                            .route_id
                                    }

                                    group={group}

                                    columns={
                                        visibleColumns
                                    }

                                    subColumns={subColumns.filter(
                                        column =>
                                            column.show !==
                                            false
                                    )}

                                    clickViewHandler={
                                        clickViewHandler
                                    }

                                    clickExportHandler={
                                        clickExportHandler
                                    }

                                    clickDeleteHandler={
                                        clickDeleteHandler
                                    }

                                    clickInfoHandler={
                                        clickInfoHandler
                                    }

                                    clickSubRouteViewHandler={
                                        clickSubRouteViewHandler
                                    }

                                    openLogs={
                                        openLogs
                                    }
                                />

                            ))

                        )}

                    </TableBody>

                </Table>


                {/* =========================================================
                    Pagination
                ========================================================= */}

                {count > rowsPerPage && (
                    <Box
                        sx={{
                            width: '100%'
                        }}
                    >
                        <TablePaginationActions
                            count={count}
                            page={page}
                            rowsPerPage={
                                rowsPerPage
                            }
                            onPageChange={
                                handleChangePage
                            }
                            onRowsPerPageChange={
                                onRowsPerPageChange
                            }
                        />
                    </Box>
                )}

            </MDBox>


            {/* =============================================================
                Column Menu
            ============================================================= */}

            <Menu
                anchorEl={
                    columnMenuAnchorEl
                }
                open={
                    Boolean(
                        columnMenuAnchorEl
                    )
                }
                onClose={
                    handleCloseColumnMenu
                }
                PaperProps={{
                    sx: {
                        minWidth: 190
                    }
                }}
            >

                <MenuItem
                    onClick={() => {
                        if (
                            columnMenuColumn
                        ) {
                            handleSort(
                                columnMenuColumn
                                    .accessor
                            );
                        }

                        handleCloseColumnMenu();
                    }}
                >
                    <ArrowUpwardIcon
                        sx={{
                            fontSize: 18,
                            mr: 1
                        }}
                    />
                    Sort
                    {sortModel?.field ===
                        columnMenuColumn?.accessor &&
                        sortModel?.direction ===
                        'asc'
                        ? ' Descending'
                        : ' Ascending'}
                </MenuItem>


                <MenuItem
                    onClick={() => {

                        if (
                            columnMenuColumn
                        ) {
                            handleOpenFilter(
                                {
                                    currentTarget:
                                        columnMenuAnchorEl
                                },
                                columnMenuColumn
                            );
                        }
                    }}
                >
                    <FilterListIcon
                        sx={{
                            fontSize: 18,
                            mr: 1
                        }}
                    />
                    Filter
                </MenuItem>


                <Divider />


                <MenuItem
                    onClick={
                        handleHideColumn
                    }
                >
                    <ViewColumnIcon
                        sx={{
                            fontSize: 18,
                            mr: 1
                        }}
                    />
                    Hide column
                </MenuItem>

            </Menu>


            {/* =============================================================
                Filter
            ============================================================= */}

            <ColumnFilterPopover
                anchorEl={
                    filterAnchorEl
                }

                column={
                    activeFilterColumn
                }

                value={
                    temporaryFilterValue
                }

                onChange={
                    setTemporaryFilterValue
                }

                onApply={
                    handleApplyFilter
                }

                onClear={
                    handleClearFilter
                }

                onClose={() =>
                    setFilterAnchorEl(null)
                }
            />

        </TableContainer>
    );
}